import { Dialog, DialogTitle, IconButton, DialogContent, Breakpoint } from '@mui/material'
import { Close } from '@mui/icons-material'
import React, { createContext, Dispatch, useContext, useState } from 'react'
import { useConfirmDialogContext } from '../../utils/providers/confirmDialogContext'
import { useAppTranslations } from '../../utils/hooks/useAppTranslations'
import { useIsMobileLayout } from '../../utils/hooks/useIsMobileLayout'

type ContextValue = [boolean, React.Dispatch<React.SetStateAction<boolean>>]

const FormDialogContext = createContext<ContextValue | null>(null)
FormDialogContext.displayName = 'FormDialogContext'

export type FormDialogContextProviderProps = React.PropsWithChildren<{
  state: ContextValue
}>

export const FormDialogContextProvider = ({ children, state }: FormDialogContextProviderProps): React.ReactElement => {
  return <FormDialogContext.Provider value={state}>{children}</FormDialogContext.Provider>
}

export function useFormDialogContext(): ContextValue {
  const value = useContext(FormDialogContext)
  if (!value) throw new Error('FormDialog was not used')
  return value
}

export const FormDialog: React.FC<
  React.PropsWithChildren<{
    open: boolean
    setOpen: Dispatch<React.SetStateAction<boolean>>
    title: string
    maxWidth?: Breakpoint | false
    fullWidth?: boolean
    fullScreen?: boolean
    isTouched?: boolean
    greyDisplay?: boolean
  }>
> = ({
  open,
  setOpen,
  title,
  children,
  maxWidth = 'lg',
  fullWidth = true,
  fullScreen = false,
  greyDisplay = false,
}) => {
  const t = useAppTranslations()
  const useConfirmDialog = useConfirmDialogContext()
  const [hasChanged, setHasChanged] = useState(false)
  const isMobile = useIsMobileLayout()
  const isFullScreen = fullScreen || isMobile

  const handleClose = (): void => {
    if (hasChanged)
      useConfirmDialog({
        message: t('commons.closeDialogWithoutSaving'),
        buttonLabel: t('commons.actions.quitWithoutSaving'),
        onSubmit: () => setOpen(false),
      })
    else setOpen(false)
  }

  return (
    <FormDialogContextProvider state={[hasChanged, setHasChanged]}>
      <Dialog
        maxWidth={maxWidth}
        fullWidth={fullWidth}
        fullScreen={isFullScreen}
        open={open}
        onClose={handleClose}
        slotProps={
          isFullScreen
            ? {
                paper: {
                  sx: {
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    ...(isMobile
                      ? {
                          borderRadius: 0,
                          height: '100%',
                          margin: 0,
                          maxHeight: '100%',
                          maxWidth: '100%',
                          width: '100%',
                        }
                      : {
                          height: 'calc(100vh - 32px)',
                          margin: 2,
                          maxHeight: 'calc(100vh - 32px)',
                          maxWidth: 'calc(100vw - 32px)',
                          width: 'calc(100vw - 32px)',
                        }),
                  },
                },
              }
            : undefined
        }
      >
        <DialogTitle sx={{ flexShrink: 0, fontSize: 'h3.fontSize' }}>{title}</DialogTitle>
        <IconButton
          aria-label="close dialog button"
          onClick={handleClose}
          sx={(theme) => ({
            position: 'absolute',
            right: 12,
            top: 9,
            color: theme.palette.grey[500],
          })}
        >
          <Close />
        </IconButton>
        <DialogContent
          sx={{
            ...(greyDisplay ? { bgcolor: 'grey.50' } : {}),
            ...(fullScreen
              ? {
                  display: 'flex',
                  flex: 1,
                  flexDirection: 'column',
                  minHeight: 0,
                  overflow: 'hidden',
                }
              : isMobile
                ? { flex: 1, minHeight: 0, overflowY: 'auto' }
                : {}),
          }}
        >
          {children}
        </DialogContent>
      </Dialog>
    </FormDialogContextProvider>
  )
}
